import {
  GetApiTokenListParams,
  getGetApiTokenListQueryKey,
  PaginatedApiTokenDTOListItems,
  useDeleteApiTokenById,
  useGetApiTokenList,
  UserType,
} from "@bvhi/backend-api";
import { Card, CardContent, CardHeader, GridPaginationModel, GridSortModel } from "@bvhi/ui-base";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { forwardRef, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { BVHIDataGrid } from "../../../../../../components/datagrid/bvhi-datagrid";
import { useAuthContext } from "../../../../../../context/use-auth-context";
import { SecuredDeleteDialog } from "../../../../../../dialogs/secured-delete-dialog";
import { useToast } from "../../../../../../hooks/use-toast";
import { Route } from "../../../../../../routes/_main/app/api-token.index";
import { toNarrowedTypeOrNull } from "../../../../../../utils/ts";
import { useApiTokenDataGridDefinitions } from "../../hooks/use-api-token-data-grid-definition";

interface Props {
  subHeader: ReactNode;
  requestParams: GetApiTokenListParams;
  updateCallback: (paginationModel: GridPaginationModel, sortModel: GridSortModel) => void;
}

export const ApiTokenDataGrid = forwardRef<HTMLDivElement, Props>(({ requestParams, updateCallback, subHeader }, ref) => {
  const { t } = useTranslation(["api-token", "dialogs"]);
  const { page, size } = Route.useSearch();
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const { mutate: deleteApiTokenById } = useDeleteApiTokenById();

  const { data, isFetching } = useGetApiTokenList(currentUser?.organisation?.id ?? "", requestParams, {
    query: { throwOnError: (error) => error.status === 500 || error.status === 403, placeholderData: keepPreviousData },
  });

  const [apiTokenIdToDelete, setApiTokenIdToDelete] = useState<string | null>(null);
  const { pushDeleteSuccessToast, pushDeleteErrorToast } = useToast();

  const handleDelete = useCallback(
    (apiTokenIdToDelete: string) => {
      setApiTokenIdToDelete(apiTokenIdToDelete);
    },
    [setApiTokenIdToDelete]
  );

  const { columns } = useApiTokenDataGridDefinitions(handleDelete, currentUser?.type ?? UserType.AUDIOLOGIST);

  const handleConfirmDialogClose = useCallback(
    (confirmed: boolean) => {
      if (!confirmed || !apiTokenIdToDelete) {
        setApiTokenIdToDelete(null);
        return;
      }
      if (!currentUser?.organisation?.id) return;
      deleteApiTokenById(
        { organisationId: currentUser?.organisation?.id ?? "", tokenId: apiTokenIdToDelete },
        {
          onSuccess: () => {
            pushDeleteSuccessToast(t("api-token:api.token.entity"));
          },
          onError: () => {
            pushDeleteErrorToast(t("api-token:api.token.entity"));
          },
          onSettled: () => {
            setApiTokenIdToDelete(null);
            void queryClient.invalidateQueries({ queryKey: getGetApiTokenListQueryKey(currentUser?.organisation?.id ?? "") });
          },
        }
      );
    },
    [apiTokenIdToDelete, currentUser, deleteApiTokenById, queryClient, pushDeleteSuccessToast, t, pushDeleteErrorToast]
  );

  const apiTokens = toNarrowedTypeOrNull<PaginatedApiTokenDTOListItems>(data?.data, "numberOfItems");

  return (
    <div ref={ref}>
      <Card sx={{ minHeight: 300 }}>
        <CardHeader subheader={subHeader} title={t("api-token:api.token.datagrid.title")} />
        <CardContent restrictContentWidth={false}>
          <BVHIDataGrid
            columns={columns}
            disableColumnSorting
            getRowHeight={() => "auto"}
            initalPaginationModel={{ page: page ? page : 0, pageSize: size ? size : 25 }}
            loading={isFetching}
            rowCount={apiTokens?.numberOfItems ?? -1}
            rows={apiTokens?.payload ?? []}
            totalPages={apiTokens?.numberOfPages ?? 0}
            updateCallback={updateCallback}
          />
        </CardContent>
      </Card>
      <SecuredDeleteDialog
        onClose={handleConfirmDialogClose}
        open={apiTokenIdToDelete !== null}
        overwrite={{ title: t("dialogs:dialogs.delete.api.token.title") }}
      />
    </div>
  );
});
ApiTokenDataGrid.displayName = "ApiTokenDataGrid";
